import {
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { ButtonHTMLAttributes, FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CodedStatusBadge } from '../../components/ui/CodedStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DraggableModalPanel } from '../../components/ui/DraggableModalPanel';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { listSalesProducts, SalesProductSummary } from '../../services/salesProductsApi';
import {
  TechnologyApplication,
  listTechnologyApplications,
} from '../../services/technologyApi';
import {
  TechnologyDeployment,
  TechnologyRelease,
  TechnologyReleaseItem,
  addTechnologyReleaseItem,
  cancelTechnologyDeployment,
  completeTechnologyDeployment,
  createTechnologyRelease,
  deleteTechnologyDeployment,
  deleteTechnologyReleaseItem,
  failTechnologyDeployment,
  importTechnologyReleaseFromAzure,
  listTechnologyDeployments,
  listTechnologyReleases,
  refreshTechnologyReleaseAzureItems,
  reopenTechnologyDeployment,
  rescheduleTechnologyDeployment,
  scheduleTechnologyDeployment,
  startTechnologyDeployment,
  updateTechnologyRelease,
} from '../../services/technologyDeploymentsApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import {
  fromLocalDateTimeInputValue,
  toLocalDateTimeInputValue,
} from '../../utils/localDateTimeInput';
import {
  TECHNOLOGY_DEPLOYMENT_STATUS_OPTIONS,
  TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS,
  TECHNOLOGY_RELEASE_ITEM_TYPE_OPTIONS,
  TECHNOLOGY_RELEASE_STATUS_OPTIONS,
  technologyOptionLabel,
} from './technologyFormOptions';

const PAGE_SIZE = 10;

const EDITABLE_RELEASE_STATUSES = new Set(['Draft', 'Planned', 'Building']);

const FINAL_DEPLOYMENT_STATUSES = new Set(['Succeeded', 'Failed', 'Cancelled', 'RolledBack']);

const REOPEN_COMMENT_MAX_LENGTH = 50;

const RELEASE_STATUS_TRANSITIONS: Record<string, string[]> = {
  Draft: ['Draft', 'Planned', 'Cancelled'],
  Planned: ['Draft', 'Planned', 'Building', 'Cancelled'],
  Building: ['Building', 'Ready', 'Cancelled'],
};

const emptyReleaseEditState = {
  id: '',
  applicationIds: [] as string[],
  version: '',
  title: '',
  status: 'Planned',
  notes: '',
};

function isReleaseEditable(status?: string | null): boolean {
  return !!status && EDITABLE_RELEASE_STATUSES.has(status);
}

function releaseStatusChoices(current: string) {
  const allowed = new Set(RELEASE_STATUS_TRANSITIONS[current] ?? [current]);
  return TECHNOLOGY_RELEASE_STATUS_OPTIONS.filter((option) => allowed.has(option.value));
}

function ActionIconButton({
  hint,
  children,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { hint: string; children: ReactNode }) {
  return (
    <span title={hint} className="inline-flex">
      <button
        type="button"
        aria-label={hint}
        className={`inline-flex items-center justify-center rounded-md p-1.5 text-secondary-500 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${className || 'hover:bg-secondary-100 hover:text-secondary-800'}`}
        {...props}
      >
        {children}
      </button>
    </span>
  );
}

function HintButton({
  hint,
  className,
  type = 'button',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { hint: string }) {
  return (
    <span title={hint} className="inline-flex">
      <button type={type} className={className} {...props}>
        {children}
      </button>
    </span>
  );
}

const emptyIdRow = [''];

function collectPositiveIds(values: string[]): number[] {
  const unique: number[] = [];
  for (const raw of values) {
    const parsed = Number.parseInt(raw.trim(), 10);
    if (Number.isFinite(parsed) && parsed > 0 && unique.indexOf(parsed) < 0) {
      unique.push(parsed);
    }
  }
  return unique;
}

function AzureIdList({
  label,
  values,
  placeholder,
  addLabel,
  buttonLabel,
  onChange,
}: {
  label: string;
  values: string[];
  placeholder: string;
  addLabel: string;
  buttonLabel: string;
  onChange: (values: string[]) => void;
}) {
  const rows = values.length > 0 ? values : emptyIdRow;

  function updateRow(index: number, raw: string): void {
    const pasted = raw.split(/[\s,;]+/).map((part) => part.replace(/\D/g, '')).filter(Boolean);
    const next = [...rows];
    if (pasted.length > 1) {
      next.splice(index, 1, ...pasted);
    } else {
      next[index] = raw.replace(/\D/g, '');
    }
    onChange(next);
  }

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-secondary-800">{label}</legend>
      <div className="space-y-2">
        {rows.map((value, index) => (
          <div
            key={`${label}-${index}`}
            className="flex items-center gap-2 rounded-lg border border-secondary-200 bg-secondary-50 p-2"
          >
            <span className="w-6 shrink-0 text-center text-xs font-medium text-secondary-500">
              {index + 1}
            </span>
            <input
              className="erp-input min-w-0 flex-1"
              inputMode="numeric"
              value={value}
              placeholder={placeholder}
              aria-label={`${label} ${index + 1}`}
              onChange={(event) => updateRow(index, event.target.value)}
            />
            <span title="Remove este ID." className="inline-flex">
              <DeleteIconButton
                label="Remove este ID."
                onClick={() => {
                  const next = rows.filter((_, rowIndex) => rowIndex !== index);
                  onChange(next.length > 0 ? next : [...emptyIdRow]);
                }}
              />
            </span>
          </div>
        ))}
      </div>
      <HintButton
        hint={addLabel}
        className="erp-btn-secondary"
        onClick={() => onChange([...rows, ''])}
      >
        {buttonLabel}
      </HintButton>
    </fieldset>
  );
}

function ReleaseScopeItems({
  items,
  canRemove,
  onRemove,
}: {
  items: TechnologyReleaseItem[];
  canRemove: boolean;
  onRemove: (item: TechnologyReleaseItem) => void;
}) {
  return (
    <ul className="divide-y divide-secondary-100">
      {items.map((item) => (
        <li key={item.id} className="flex items-start justify-between gap-2 px-3 py-2 text-sm">
          <div>
            <p className="font-medium text-secondary-900">{item.title}</p>
            <p className="text-xs text-secondary-500">
              {technologyOptionLabel(TECHNOLOGY_RELEASE_ITEM_TYPE_OPTIONS, item.itemType)}
              {item.externalId ? ` · ${item.externalId}` : null}
              {item.stateSnapshot ? ` · ${item.stateSnapshot}` : null}
            </p>
            {item.url ? (
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary-700 underline"
              >
                Abrir no Azure
              </a>
            ) : null}
          </div>
          {canRemove ? (
            <span title="Remove este item do escopo da release." className="inline-flex">
              <DeleteIconButton
                label="Remove este item do escopo da release."
                onClick={() => onRemove(item)}
              />
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR');
}

function releasePackageLabel(release: TechnologyRelease): string {
  const names = (release.applications ?? [])
    .map((item) => item.applicationName)
    .filter(Boolean);
  const apps = names.length > 0 ? names.join(', ') : 'Pacote';
  return `${apps} · ${release.version} — ${release.title}`;
}

const emptyReleaseState = {
  applicationIds: [] as string[],
  version: '',
  title: '',
  status: 'Planned',
  notes: '',
};

export function TechnologyDeploymentsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('technology.deployments');

  const [deployments, setDeployments] = useState<TechnologyDeployment[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [acting, setActing] = useState(false);

  const [applications, setApplications] = useState<TechnologyApplication[]>([]);
  const [filterApplications, setFilterApplications] = useState<TechnologyApplication[]>([]);
  const [products, setProducts] = useState<SalesProductSummary[]>([]);
  const [releases, setReleases] = useState<TechnologyRelease[]>([]);

  const [salesProductFilter, setSalesProductFilter] = useState('');
  const [applicationFilter, setApplicationFilter] = useState('');
  const [environmentFilter, setEnvironmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [scheduledFromLocal, setScheduledFromLocal] = useState('');
  const [scheduledToLocal, setScheduledToLocal] = useState('');
  const [upcomingOnly, setUpcomingOnly] = useState(true);

  const scheduleForm = useCollapsibleItemForm();
  const releaseForm = useCollapsibleItemForm();
  const releaseEditForm = useCollapsibleItemForm();
  const itemForm = useCollapsibleItemForm();
  const azureImportForm = useCollapsibleItemForm();

  const [scheduleState, setScheduleState] = useState({
    releaseId: '',
    environmentKind: '',
    scheduledAtLocal: toLocalDateTimeInputValue(new Date().toISOString()),
    resultNotes: '',
  });
  const [releaseState, setReleaseState] = useState(emptyReleaseState);
  const [releaseEditState, setReleaseEditState] = useState(emptyReleaseEditState);
  const [itemState, setItemState] = useState({
    releaseId: '',
    itemType: 'Note',
    title: '',
    externalId: '',
    url: '',
  });
  const [azureImportState, setAzureImportState] = useState({
    applicationId: '',
    workItemIds: [...emptyIdRow],
    pullRequestIds: [...emptyIdRow],
  });
  const [rescheduleLocal, setRescheduleLocal] = useState('');
  const [saving, setSaving] = useState(false);
  const [azureItemsModalOpen, setAzureItemsModalOpen] = useState(false);
  const [reopenTarget, setReopenTarget] = useState<TechnologyDeployment | null>(null);
  const [reopenComment, setReopenComment] = useState('');

  const selected = useMemo(
    () => deployments.find((item) => item.id === selectedId) ?? null,
    [deployments, selectedId],
  );
  const selectedRelease = useMemo(
    () => releases.find((item) => item.id === selected?.releaseId) ?? null,
    [releases, selected?.releaseId],
  );
  const editableReleases = useMemo(
    () => releases.filter((item) => isReleaseEditable(item.status)),
    [releases],
  );
  const releaseLocked = selectedRelease != null && !isReleaseEditable(selectedRelease.status);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  async function loadOptions(): Promise<void> {
    try {
      const [apps, productResult, releaseResult] = await Promise.all([
        listTechnologyApplications({ page: 1, pageSize: 200 }),
        listSalesProducts({ activeOnly: true, page: 1, pageSize: 200 }),
        listTechnologyReleases({ page: 1, pageSize: 200 }),
      ]);
      setApplications(apps.items);
      setProducts(productResult.items);
      setReleases(releaseResult.items);
    } catch {
      /* opções auxiliares */
    }
  }

  async function loadFilterOptions(): Promise<void> {
    try {
      const apps = await listTechnologyApplications({
        page: 1,
        pageSize: 200,
        salesProductId: salesProductFilter || undefined,
      });
      setFilterApplications(apps.items);
    } catch {
      setFilterApplications([]);
    }
  }

  async function loadDeployments(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const fromUtc = scheduledFromLocal
        ? fromLocalDateTimeInputValue(`${scheduledFromLocal}T00:00`)
        : undefined;
      const toUtc = scheduledToLocal
        ? fromLocalDateTimeInputValue(`${scheduledToLocal}T23:59`)
        : undefined;

      const result = await listTechnologyDeployments({
        page: targetPage,
        pageSize: PAGE_SIZE,
        salesProductId: salesProductFilter || undefined,
        applicationId: applicationFilter || undefined,
        environmentKind: environmentFilter || undefined,
        status: statusFilter || undefined,
        scheduledFromUtc: fromUtc || undefined,
        scheduledToUtc: toUtc || undefined,
        upcomingOnly,
      });
      setDeployments(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
      if (selectedId && !result.items.some((item) => item.id === selectedId)) {
        setSelectedId(result.items[0]?.id ?? null);
      } else if (!selectedId && result.items.length > 0) {
        setSelectedId(result.items[0].id);
      }
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar implantações.'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadOptions();
  }, []);

  useEffect(() => {
    void loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesProductFilter, applicationFilter]);

  useEffect(() => {
    void loadDeployments(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    salesProductFilter,
    applicationFilter,
    environmentFilter,
    statusFilter,
    scheduledFromLocal,
    scheduledToLocal,
    upcomingOnly,
  ]);

  useEffect(() => {
    void loadDeployments(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    setAzureItemsModalOpen(false);
  }, [selectedId]);

  useEffect(() => {
    if (selected) {
      setRescheduleLocal(toLocalDateTimeInputValue(selected.scheduledAtUtc));
      setItemState((current) => ({ ...current, releaseId: selected.releaseId }));
      const release = releases.find((item) => item.id === selected.releaseId);
      const firstAppId = release?.applications?.[0]?.applicationId ?? selected.applicationId ?? '';
      setAzureImportState((current) => ({
        ...current,
        applicationId: current.applicationId || firstAppId || '',
      }));
    }
  }, [selected, releases]);

  async function refreshAll(): Promise<void> {
    await Promise.all([loadOptions(), loadFilterOptions(), loadDeployments(page)]);
  }

  async function handleSchedule(event: FormEvent): Promise<void> {
    event.preventDefault();
    const scheduledAtUtc = fromLocalDateTimeInputValue(scheduleState.scheduledAtLocal);
    if (!scheduledAtUtc || !scheduleState.releaseId || !scheduleState.environmentKind) {
      toast.error('Informe release, ambiente e data planejada.');
      return;
    }

    setSaving(true);
    try {
      const created = await scheduleTechnologyDeployment({
        releaseId: scheduleState.releaseId,
        environmentKind: scheduleState.environmentKind,
        scheduledAtUtc,
        resultNotes: scheduleState.resultNotes.trim() || null,
      });
      toast.success('Implantação agendada.');
      scheduleForm.handleCreateSuccess(() =>
        setScheduleState({
          releaseId: '',
          environmentKind: '',
          scheduledAtLocal: toLocalDateTimeInputValue(new Date().toISOString()),
          resultNotes: '',
        }),
      );
      setUpcomingOnly(true);
      await refreshAll();
      setSelectedId(created.id);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao agendar implantação.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateRelease(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (
      releaseState.applicationIds.length === 0 ||
      !releaseState.version.trim() ||
      !releaseState.title.trim()
    ) {
      toast.error('Informe ao menos uma aplicação, versão e título.');
      return;
    }

    setSaving(true);
    try {
      const created = await createTechnologyRelease({
        applications: releaseState.applicationIds.map((applicationId, index) => ({
          applicationId,
          sortOrder: index,
        })),
        version: releaseState.version.trim(),
        title: releaseState.title.trim(),
        status: releaseState.status,
        notes: releaseState.notes.trim() || null,
      });
      toast.success('Release criada.');
      releaseForm.handleCreateSuccess(() => setReleaseState(emptyReleaseState));
      await loadOptions();
      setScheduleState((current) => ({ ...current, releaseId: created.id }));
      setItemState((current) => ({ ...current, releaseId: created.id }));
      scheduleForm.expandForm();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao criar release.'));
    } finally {
      setSaving(false);
    }
  }

  function fillReleaseEdit(releaseId: string): void {
    const release = releases.find((item) => item.id === releaseId);
    if (!release || !isReleaseEditable(release.status)) {
      setReleaseEditState(emptyReleaseEditState);
      return;
    }

    setReleaseEditState({
      id: release.id,
      applicationIds: [...release.applications]
        .sort((left, right) => left.sortOrder - right.sortOrder)
        .map((item) => item.applicationId),
      version: release.version,
      title: release.title,
      status: release.status,
      notes: release.notes ?? '',
    });
  }

  function openReleaseEdit(releaseId: string): void {
    fillReleaseEdit(releaseId);
    releaseEditForm.expandForm();
  }

  function toggleReleaseEditApplication(applicationId: string): void {
    setReleaseEditState((current) => {
      const selectedIds = current.applicationIds.includes(applicationId);
      return {
        ...current,
        applicationIds: selectedIds
          ? current.applicationIds.filter((id) => id !== applicationId)
          : [...current.applicationIds, applicationId],
      };
    });
  }

  async function handleUpdateRelease(event: FormEvent): Promise<void> {
    event.preventDefault();
    const current = releases.find((item) => item.id === releaseEditState.id);
    if (
      !current ||
      !isReleaseEditable(current.status) ||
      releaseEditState.applicationIds.length === 0 ||
      !releaseEditState.version.trim() ||
      !releaseEditState.title.trim()
    ) {
      toast.error('Informe ao menos uma aplicação, versão e título.');
      return;
    }

    setSaving(true);
    try {
      await updateTechnologyRelease(current.id, {
        applications: releaseEditState.applicationIds.map((applicationId, index) => ({
          applicationId,
          componentVersion:
            current.applications.find((item) => item.applicationId === applicationId)
              ?.componentVersion ?? null,
          sortOrder: index,
        })),
        version: releaseEditState.version.trim(),
        title: releaseEditState.title.trim(),
        status: releaseEditState.status,
        notes: releaseEditState.notes.trim() || null,
        summaryHtml: current.summaryHtml ?? null,
        plannedAtUtc: current.plannedAtUtc ?? null,
        externalBuildId: current.externalBuildId ?? null,
        externalBuildUrl: current.externalBuildUrl ?? null,
      });
      toast.success('Release atualizada.');
      releaseEditForm.handleCreateSuccess(() => setReleaseEditState(emptyReleaseEditState));
      await refreshAll();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao atualizar release.'));
    } finally {
      setSaving(false);
    }
  }

  function toggleReleaseApplication(applicationId: string): void {
    setReleaseState((current) => {
      const selected = current.applicationIds.includes(applicationId);
      return {
        ...current,
        applicationIds: selected
          ? current.applicationIds.filter((id) => id !== applicationId)
          : [...current.applicationIds, applicationId],
      };
    });
  }

  async function handleAddItem(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!itemState.releaseId || !itemState.title.trim()) {
      toast.error('Informe a release e o título do item.');
      return;
    }

    setSaving(true);
    try {
      await addTechnologyReleaseItem(itemState.releaseId, {
        itemType: itemState.itemType,
        title: itemState.title.trim(),
        externalId: itemState.externalId.trim() || null,
        url: itemState.url.trim() || null,
        includeInCommunications: true,
        sortOrder: 0,
      });
      toast.success('Item adicionado ao escopo.');
      itemForm.handleCreateSuccess(() =>
        setItemState((current) => ({
          ...current,
          title: '',
          externalId: '',
          url: '',
          itemType: 'Note',
        })),
      );
      await refreshAll();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao adicionar item.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteItem(item: TechnologyReleaseItem): Promise<void> {
    if (!selected) return;
    const ok = await confirm({
      title: 'Remover item',
      message: `Remover "${item.title}" do escopo desta versão?`,
      confirmLabel: 'Remover',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      await deleteTechnologyReleaseItem(selected.releaseId, item.id);
      toast.success('Item removido.');
      await refreshAll();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao remover item.'));
    }
  }

  async function handleAzureImport(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!selected?.releaseId) {
      toast.error('Selecione uma implantação vinculada a uma release.');
      return;
    }
    if (!azureImportState.applicationId) {
      toast.error('Selecione a aplicação do pacote.');
      return;
    }

    const workItemIds = collectPositiveIds(azureImportState.workItemIds);
    const pullRequestIds = collectPositiveIds(azureImportState.pullRequestIds);
    if (workItemIds.length === 0 && pullRequestIds.length === 0) {
      toast.error('Informe IDs de work items e/ou pull requests.');
      return;
    }

    setSaving(true);
    try {
      const result = await importTechnologyReleaseFromAzure(selected.releaseId, {
        applicationId: azureImportState.applicationId,
        workItemIds,
        pullRequestIds,
      });
      const parts = [
        result.importedCount > 0 ? `${result.importedCount} importado(s)` : null,
        result.skippedCount > 0 ? `${result.skippedCount} já existente(s)` : null,
      ].filter(Boolean);
      toast.success(parts.length > 0 ? parts.join(' · ') : 'Importação concluída.');
      if (result.errors.length > 0) {
        toast.warn(result.errors.slice(0, 3).join(' · '));
      }
      azureImportForm.handleCreateSuccess(() =>
        setAzureImportState((current) => ({
          ...current,
          workItemIds: [...emptyIdRow],
          pullRequestIds: [...emptyIdRow],
        })),
      );
      await refreshAll();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao importar do Azure DevOps.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleAzureRefresh(): Promise<void> {
    if (!selected?.releaseId) return;

    setSaving(true);
    try {
      const result = await refreshTechnologyReleaseAzureItems(selected.releaseId);
      const parts = [
        result.updatedCount > 0 ? `${result.updatedCount} status atualizado(s)` : null,
        result.importedCount > 0 ? `${result.importedCount} work item(s) da pull request` : null,
      ].filter(Boolean);
      toast.success(parts.length > 0 ? parts.join(' · ') : 'Nenhum item atualizado.');
      if (result.errors.length > 0) {
        toast.warn(result.errors.slice(0, 3).join(' · '));
      }
      await refreshAll();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao atualizar status do Azure.'));
    } finally {
      setSaving(false);
    }
  }

  async function runAction(
    label: string,
    action: () => Promise<TechnologyDeployment>,
  ): Promise<void> {
    setActing(true);
    try {
      const updated = await action();
      toast.success(label);
      await refreshAll();
      setSelectedId(updated.id);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao atualizar implantação.'));
    } finally {
      setActing(false);
    }
  }

  async function handleReschedule(): Promise<void> {
    if (!selected) return;
    const scheduledAtUtc = fromLocalDateTimeInputValue(rescheduleLocal);
    if (!scheduledAtUtc) {
      toast.error('Informe a nova data planejada.');
      return;
    }

    await runAction('Implantação reagendada.', () =>
      rescheduleTechnologyDeployment(selected.id, { scheduledAtUtc }),
    );
  }

  function openReopen(deployment: TechnologyDeployment): void {
    setReopenComment('');
    setReopenTarget(deployment);
  }

  async function handleReopenSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!reopenTarget) return;

    const comment = reopenComment.trim();
    if (!comment) {
      toast.error('Informe um comentário para reabrir a implantação.');
      return;
    }

    if (comment.length > REOPEN_COMMENT_MAX_LENGTH) {
      toast.error(`O comentário deve ter no máximo ${REOPEN_COMMENT_MAX_LENGTH} caracteres.`);
      return;
    }

    const deploymentId = reopenTarget.id;
    setActing(true);
    try {
      const updated = await reopenTechnologyDeployment(deploymentId, { comment });
      toast.success('Implantação reaberta.');
      setReopenTarget(null);
      setReopenComment('');
      await refreshAll();
      setSelectedId(updated.id);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao reabrir a implantação.'));
    } finally {
      setActing(false);
    }
  }

  async function handleDeleteDeployment(deployment: TechnologyDeployment): Promise<void> {
    const ok = await confirm({
      title: 'Excluir implantação',
      message: `Remover o registro da implantação de ${deployment.releaseVersion ?? 'versão'} em ${technologyOptionLabel(TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS, deployment.environmentKind)}?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!ok) return;

    setActing(true);
    try {
      await deleteTechnologyDeployment(deployment.id);
      toast.success('Registro excluído.');
      if (selectedId === deployment.id) {
        setSelectedId(null);
      }
      await refreshAll();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao excluir.'));
    } finally {
      setActing(false);
    }
  }

  function renderDeploymentActions(deployment: TechnologyDeployment) {
    if (!canWrite) return null;
    const release = releases.find((item) => item.id === deployment.releaseId);
    return (
      <div className="flex shrink-0 items-center gap-1">
        {release && isReleaseEditable(release.status) ? (
          <ActionIconButton
            hint="Editar release. Altera aplicações, versão, status, título e notas."
            onClick={() => openReleaseEdit(deployment.releaseId)}
          >
            <PencilSquareIcon className="h-5 w-5" />
          </ActionIconButton>
        ) : null}
        {deployment.status === 'Scheduled' ? (
          <>
            <ActionIconButton
              hint="Iniciar. Marca a implantação como em andamento. Concluir fica disponível depois disso."
              className="hover:bg-emerald-50 hover:text-emerald-700"
              disabled={acting}
              onClick={() =>
                void runAction('Implantação iniciada.', () =>
                  startTechnologyDeployment(deployment.id),
                )
              }
            >
              <PlayIcon className="h-5 w-5" />
            </ActionIconButton>
            <ActionIconButton
              hint="Cancelar. Cancela esta implantação agendada. A release continua cadastrada."
              className="hover:bg-amber-50 hover:text-amber-700"
              disabled={acting}
              onClick={() =>
                void runAction('Implantação cancelada.', () =>
                  cancelTechnologyDeployment(deployment.id),
                )
              }
            >
              <NoSymbolIcon className="h-5 w-5" />
            </ActionIconButton>
          </>
        ) : null}
        {FINAL_DEPLOYMENT_STATUSES.has(deployment.status) ? (
          <ActionIconButton
            hint="Reabrir. Volta a implantação para agendada. O comentário é obrigatório e tem no máximo 50 caracteres."
            className="hover:bg-sky-50 hover:text-sky-700"
            disabled={acting}
            onClick={() => openReopen(deployment)}
          >
            <ArrowUturnLeftIcon className="h-5 w-5" />
          </ActionIconButton>
        ) : null}
        {deployment.status === 'InProgress' ? (
          <>
            <ActionIconButton
              hint="Concluir. Registra que a implantação terminou. Em produção, a release é liberada e deixa de poder ser editada."
              className="hover:bg-emerald-50 hover:text-emerald-700"
              disabled={acting}
              onClick={() =>
                void runAction('Implantação concluída.', () =>
                  completeTechnologyDeployment(deployment.id),
                )
              }
            >
              <CheckCircleIcon className="h-5 w-5" />
            </ActionIconButton>
            <ActionIconButton
              hint="Falhou. Registra que a implantação não terminou. A release continua editável se ainda estiver em rascunho, planejada ou em build."
              className="hover:bg-red-50 hover:text-red-600"
              disabled={acting}
              onClick={() =>
                void runAction('Implantação marcada como falha.', () =>
                  failTechnologyDeployment(deployment.id),
                )
              }
            >
              <ExclamationTriangleIcon className="h-5 w-5" />
            </ActionIconButton>
          </>
        ) : null}
        {deployment.status !== 'InProgress' ? (
          <DeleteIconButton
            label="Excluir. Remove o registro desta implantação. A release continua cadastrada."
            disabled={acting}
            onClick={() => void handleDeleteDeployment(deployment)}
          />
        ) : null}
      </div>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title="Deploys"
        description="Planeje e acompanhe implantações: quando, em qual ambiente e o que entra em cada versão."
        actions={
          canWrite ? (
            <>
              <HintButton
                hint="Cria o pacote da versão com aplicações, título e notas."
                className="erp-btn-primary"
                onClick={() =>
                  releaseForm.toggleOrCancel(() => setReleaseState(emptyReleaseState))
                }
              >
                Nova release
              </HintButton>
              {editableReleases.length > 0 ? (
                <HintButton
                  hint="Altera aplicações, versão, status, título e notas enquanto a release está em rascunho, planejada ou em build."
                  className="erp-btn-secondary"
                  onClick={() => {
                    if (releaseEditForm.formExpanded) {
                      releaseEditForm.collapseForm(() => setReleaseEditState(emptyReleaseEditState));
                      return;
                    }
                    if (selectedRelease && isReleaseEditable(selectedRelease.status)) {
                      openReleaseEdit(selectedRelease.id);
                      return;
                    }
                    releaseEditForm.expandForm();
                  }}
                >
                  Editar release
                </HintButton>
              ) : null}
              <HintButton
                hint="Agenda uma release em um ambiente, na data informada."
                className="erp-btn-secondary"
                onClick={() =>
                  scheduleForm.toggleOrCancel(() =>
                    setScheduleState({
                      releaseId: '',
                      environmentKind: '',
                      scheduledAtLocal: toLocalDateTimeInputValue(new Date().toISOString()),
                      resultNotes: '',
                    }),
                  )
                }
              >
                Agendar implantação
              </HintButton>
            </>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-2 rounded-xl border border-secondary-200 bg-white px-4 py-3">
        <label className="erp-label min-w-[12rem]">
          Produto
          <select
            className="erp-input"
            value={salesProductFilter}
            onChange={(event) => {
              const nextProductId = event.target.value;
              setPage(1);
              setSalesProductFilter(nextProductId);
              setApplicationFilter('');
              setEnvironmentFilter('');
            }}
          >
            <option value="">Todos</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </select>
        </label>
        <label className="erp-label min-w-[12rem]">
          Aplicação
          <select
            className="erp-input"
            value={applicationFilter}
            onChange={(event) => {
              setPage(1);
              setApplicationFilter(event.target.value);
              setEnvironmentFilter('');
            }}
            disabled={Boolean(salesProductFilter) && filterApplications.length === 0}
          >
            <option value="">
              {salesProductFilter && filterApplications.length === 0
                ? 'Nenhuma no portfólio'
                : 'Todas'}
            </option>
            {filterApplications.map((application) => (
              <option key={application.id} value={application.id}>
                {application.name}
              </option>
            ))}
          </select>
        </label>
        <label className="erp-label min-w-[12rem]">
          Ambiente
          <select
            className="erp-input"
            value={environmentFilter}
            onChange={(event) => {
              setPage(1);
              setEnvironmentFilter(event.target.value);
            }}
          >
            <option value="">Todos</option>
            {TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="erp-label min-w-[10rem]">
          Status
          <select
            className="erp-input"
            value={statusFilter}
            onChange={(event) => {
              setPage(1);
              setStatusFilter(event.target.value);
            }}
          >
            <option value="">Todos</option>
            {TECHNOLOGY_DEPLOYMENT_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="erp-label min-w-[10rem]">
          De
          <input
            type="date"
            className="erp-input"
            value={scheduledFromLocal}
            onChange={(event) => {
              setPage(1);
              setScheduledFromLocal(event.target.value);
            }}
          />
        </label>
        <label className="erp-label min-w-[10rem]">
          Até
          <input
            type="date"
            className="erp-input"
            value={scheduledToLocal}
            onChange={(event) => {
              setPage(1);
              setScheduledToLocal(event.target.value);
            }}
          />
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm text-secondary-700">
          <input
            type="checkbox"
            checked={upcomingOnly}
            onChange={(event) => {
              setPage(1);
              setUpcomingOnly(event.target.checked);
            }}
          />
          Somente próximas
        </label>
      </div>

      {canWrite ? (
        <>
          <CollapsibleFormSection
            title="Nova release"
            showTrigger={false}
            expanded={releaseForm.formExpanded}
            onToggle={() => releaseForm.toggleOrCancel(() => setReleaseState(emptyReleaseState))}
          >
            <form onSubmit={(event) => void handleCreateRelease(event)} className="grid gap-3 sm:grid-cols-2">
              <fieldset className="erp-label sm:col-span-2">
                <legend className="mb-1 text-sm font-medium text-secondary-800">
                  Aplicações do pacote
                </legend>
                <div className="erp-input max-h-40 space-y-1 overflow-y-auto p-2">
                  {applications.length === 0 ? (
                    <p className="text-sm text-secondary-500">Nenhuma aplicação cadastrada.</p>
                  ) : (
                    applications.map((application) => (
                      <label
                        key={application.id}
                        className="flex items-center gap-2 text-sm font-normal"
                      >
                        <input
                          type="checkbox"
                          checked={releaseState.applicationIds.includes(application.id)}
                          onChange={() => toggleReleaseApplication(application.id)}
                        />
                        <span>{application.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </fieldset>
              <label className="erp-label">
                Versão
                <input
                  className="erp-input"
                  value={releaseState.version}
                  onChange={(event) =>
                    setReleaseState((current) => ({ ...current, version: event.target.value }))
                  }
                  placeholder="1.4.0"
                  required
                />
              </label>
              <label className="erp-label">
                Status
                <select
                  className="erp-input"
                  value={releaseState.status}
                  onChange={(event) =>
                    setReleaseState((current) => ({ ...current, status: event.target.value }))
                  }
                >
                  {TECHNOLOGY_RELEASE_STATUS_OPTIONS.filter((option) =>
                    ['Draft', 'Planned', 'Building', 'Ready'].includes(option.value),
                  ).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label sm:col-span-2">
                Título
                <input
                  className="erp-input"
                  value={releaseState.title}
                  onChange={(event) =>
                    setReleaseState((current) => ({ ...current, title: event.target.value }))
                  }
                  required
                />
              </label>
              <label className="erp-label sm:col-span-2">
                Notas
                <textarea
                  className="erp-input min-h-[3.5rem]"
                  value={releaseState.notes}
                  onChange={(event) =>
                    setReleaseState((current) => ({ ...current, notes: event.target.value }))
                  }
                />
              </label>
              <FormActionBar
                className="erp-btn-actions sm:col-span-2"
                onCancel={() => releaseForm.collapseForm(() => setReleaseState(emptyReleaseState))}
              >
                <HintButton
                  hint="Em breve: coleta os commits, gera as notas com IA e grava o texto nesta release."
                  className="erp-btn-secondary"
                  disabled
                >
                  Gerar notes com IA (em breve)
                </HintButton>
                <HintButton
                  type="submit"
                  hint="Cria o pacote da versão com as aplicações, o título e as notas. A implantação é agendada depois."
                  disabled={saving}
                  className="erp-btn-primary"
                >
                  {saving ? 'Salvando...' : 'Criar release'}
                </HintButton>
              </FormActionBar>
            </form>
          </CollapsibleFormSection>

          {editableReleases.length > 0 ? (
            <CollapsibleFormSection
              title="Editar release"
              showTrigger={false}
              expanded={releaseEditForm.formExpanded}
              isEditing={releaseEditState.id !== ''}
              editingSummary={
                releaseEditState.version
                  ? `${releaseEditState.version} — ${releaseEditState.title}`
                  : 'Escolha a release para alterar'
              }
              createSummary="Altera aplicações, versão, status, título e notas enquanto a release está em rascunho, planejada ou em build."
              onToggle={() =>
                releaseEditForm.toggleOrCancel(() => setReleaseEditState(emptyReleaseEditState))
              }
            >
              <form onSubmit={(event) => void handleUpdateRelease(event)} className="grid gap-3 sm:grid-cols-2">
                <label className="erp-label sm:col-span-2">
                  Release
                  <select
                    className="erp-input"
                    value={releaseEditState.id}
                    onChange={(event) => fillReleaseEdit(event.target.value)}
                    required
                  >
                    <option value="">Selecione</option>
                    {editableReleases.map((release) => (
                      <option key={release.id} value={release.id}>
                        {releasePackageLabel(release)} ·{' '}
                        {technologyOptionLabel(TECHNOLOGY_RELEASE_STATUS_OPTIONS, release.status)}
                      </option>
                    ))}
                  </select>
                </label>
                <fieldset className="erp-label sm:col-span-2">
                  <legend className="mb-1 text-sm font-medium text-secondary-800">
                    Aplicações do pacote
                  </legend>
                  <div className="erp-input max-h-40 space-y-1 overflow-y-auto p-2">
                    {applications.map((application) => (
                      <label
                        key={application.id}
                        className="flex items-center gap-2 text-sm font-normal"
                      >
                        <input
                          type="checkbox"
                          checked={releaseEditState.applicationIds.includes(application.id)}
                          onChange={() => toggleReleaseEditApplication(application.id)}
                        />
                        <span>{application.name}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <label className="erp-label">
                  Versão
                  <input
                    className="erp-input"
                    value={releaseEditState.version}
                    onChange={(event) =>
                      setReleaseEditState((current) => ({ ...current, version: event.target.value }))
                    }
                    required
                  />
                </label>
                <label className="erp-label">
                  Status
                  <select
                    className="erp-input"
                    value={releaseEditState.status}
                    onChange={(event) =>
                      setReleaseEditState((current) => ({ ...current, status: event.target.value }))
                    }
                  >
                    {releaseStatusChoices(
                      releases.find((item) => item.id === releaseEditState.id)?.status
                        ?? releaseEditState.status,
                    ).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="erp-label sm:col-span-2">
                  Título
                  <input
                    className="erp-input"
                    value={releaseEditState.title}
                    onChange={(event) =>
                      setReleaseEditState((current) => ({ ...current, title: event.target.value }))
                    }
                    required
                  />
                </label>
                <label className="erp-label sm:col-span-2">
                  Notas
                  <textarea
                    className="erp-input min-h-[3.5rem]"
                    value={releaseEditState.notes}
                    onChange={(event) =>
                      setReleaseEditState((current) => ({ ...current, notes: event.target.value }))
                    }
                  />
                </label>
                <FormActionBar
                  className="erp-btn-actions sm:col-span-2"
                  onCancel={() =>
                    releaseEditForm.collapseForm(() => setReleaseEditState(emptyReleaseEditState))
                  }
                >
                  <HintButton
                    type="submit"
                    hint="Grava aplicações, versão, status, título e notas. Depois de pronta, liberada ou cancelada, a release deixa de poder ser alterada."
                    disabled={saving || releaseEditState.id === ''}
                    className="erp-btn-primary"
                  >
                    {saving ? 'Salvando...' : 'Salvar release'}
                  </HintButton>
                </FormActionBar>
              </form>
            </CollapsibleFormSection>
          ) : null}

          <CollapsibleFormSection
            title="Agendar implantação"
            showTrigger={false}
            expanded={scheduleForm.formExpanded}
            onToggle={() =>
              scheduleForm.toggleOrCancel(() =>
                setScheduleState({
                  releaseId: '',
                  environmentKind: '',
                  scheduledAtLocal: toLocalDateTimeInputValue(new Date().toISOString()),
                  resultNotes: '',
                }),
              )
            }
          >
            <form onSubmit={(event) => void handleSchedule(event)} className="grid gap-3 sm:grid-cols-2">
              <label className="erp-label sm:col-span-2">
                Release
                <select
                  className="erp-input"
                  value={scheduleState.releaseId}
                  onChange={(event) =>
                    setScheduleState((current) => ({
                      ...current,
                      releaseId: event.target.value,
                    }))
                  }
                  required
                >
                  <option value="">Selecione</option>
                  {releases.map((release) => (
                    <option key={release.id} value={release.id}>
                      {releasePackageLabel(release)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Ambiente
                <select
                  className="erp-input"
                  value={scheduleState.environmentKind}
                  onChange={(event) =>
                    setScheduleState((current) => ({
                      ...current,
                      environmentKind: event.target.value,
                    }))
                  }
                  required
                >
                  <option value="">Selecione</option>
                  {TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Data planejada
                <input
                  type="datetime-local"
                  className="erp-input"
                  value={scheduleState.scheduledAtLocal}
                  onChange={(event) =>
                    setScheduleState((current) => ({
                      ...current,
                      scheduledAtLocal: event.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label className="erp-label sm:col-span-2">
                Observações
                <textarea
                  className="erp-input min-h-[4rem]"
                  value={scheduleState.resultNotes}
                  onChange={(event) =>
                    setScheduleState((current) => ({
                      ...current,
                      resultNotes: event.target.value,
                    }))
                  }
                />
              </label>
              <FormActionBar
                className="erp-btn-actions sm:col-span-2"
                onCancel={() =>
                  scheduleForm.collapseForm(() =>
                    setScheduleState({
                      releaseId: '',
                      environmentKind: '',
                      scheduledAtLocal: toLocalDateTimeInputValue(new Date().toISOString()),
                      resultNotes: '',
                    }),
                  )
                }
              >
                <HintButton
                  type="submit"
                  hint="Agenda esta release no ambiente e na data informados. O horário segue o relógio do seu computador."
                  disabled={saving}
                  className="erp-btn-primary"
                >
                  {saving ? 'Salvando...' : 'Agendar'}
                </HintButton>
              </FormActionBar>
            </form>
          </CollapsibleFormSection>
        </>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <section className="rounded-xl border border-secondary-200 bg-white">
          <div className="border-b border-secondary-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-secondary-900">
              {upcomingOnly ? 'Próximas implantações' : 'Implantações'}
            </h2>
          </div>
          {loading ? (
            <div className="p-6">
              <LoadingSpinner label="Carregando..." />
            </div>
          ) : deployments.length === 0 ? (
            <p className="px-4 py-6 text-sm text-secondary-500">Nenhuma implantação encontrada.</p>
          ) : (
            <ul className="divide-y divide-secondary-100">
              {deployments.map((item) => {
                const active = item.id === selectedId;
                return (
                  <li key={item.id} className={active ? 'bg-primary-50' : 'hover:bg-secondary-50'}>
                    <div className="flex items-start gap-2 px-3 py-3">
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 flex-col gap-1 text-left"
                        onClick={() => setSelectedId(item.id)}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-secondary-900">
                            {item.applicationName ?? 'Aplicação'} · {item.releaseVersion}
                          </span>
                          <CodedStatusBadge
                            status={item.status}
                            label={technologyOptionLabel(
                              TECHNOLOGY_DEPLOYMENT_STATUS_OPTIONS,
                              item.status,
                            )}
                          />
                        </div>
                        <p className="text-sm text-secondary-700">
                          {technologyOptionLabel(
                            TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS,
                            item.environmentKind,
                          )}
                        </p>
                        <p className="text-xs text-secondary-500">
                          Planejado {formatDateTime(item.scheduledAtUtc)}
                          {item.executedAtUtc
                            ? ` · Executado ${formatDateTime(item.executedAtUtc)}`
                            : null}
                        </p>
                      </button>
                      {renderDeploymentActions(item)}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="border-t border-secondary-100 px-2">
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={PAGE_SIZE}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          </div>
        </section>

        <section className="rounded-xl border border-secondary-200 bg-white px-4 py-4">
          {!selected ? (
            <p className="text-sm text-secondary-500">Selecione uma implantação para ver o detalhe.</p>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-base font-semibold text-secondary-900">
                    {selected.releaseTitle || selected.releaseVersion}
                  </h2>
                  <CodedStatusBadge
                    status={selected.status}
                    label={technologyOptionLabel(
                      TECHNOLOGY_DEPLOYMENT_STATUS_OPTIONS,
                      selected.status,
                    )}
                  />
                  {selectedRelease ? (
                    <CodedStatusBadge
                      status={selectedRelease.status}
                      label={technologyOptionLabel(
                        TECHNOLOGY_RELEASE_STATUS_OPTIONS,
                        selectedRelease.status,
                      )}
                    />
                  ) : null}
                </div>
                <p className="mt-1 text-sm text-secondary-600">
                  {selected.applicationName} · versão {selected.releaseVersion}
                </p>
                {selectedRelease && !isReleaseEditable(selectedRelease.status) ? (
                  <p className="mt-2 text-sm text-secondary-500">
                    Release{' '}
                    {technologyOptionLabel(
                      TECHNOLOGY_RELEASE_STATUS_OPTIONS,
                      selectedRelease.status,
                    )}
                    . Versão, título, notas e escopo podem ser alterados em rascunho, planejada e em
                    build.
                  </p>
                ) : null}
              </div>

              <dl className="grid gap-3 sm:grid-cols-2 text-sm">
                <div>
                  <dt className="text-secondary-500">Quando (planejado)</dt>
                  <dd className="font-medium text-secondary-900">
                    {formatDateTime(selected.scheduledAtUtc)}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Quando (executado)</dt>
                  <dd className="font-medium text-secondary-900">
                    {formatDateTime(selected.executedAtUtc)}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Onde</dt>
                  <dd className="font-medium text-secondary-900">
                    {technologyOptionLabel(
                      TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS,
                      selected.environmentKind,
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Aprovador</dt>
                  <dd className="font-medium text-secondary-900">
                    {selected.approvedByUserName || '—'}
                  </dd>
                </div>
              </dl>

              {selected.resultNotes ? (
                <p className="rounded-lg bg-secondary-50 px-3 py-2 text-sm text-secondary-700">
                  {selected.resultNotes}
                </p>
              ) : null}

              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-secondary-900">O que entra nesta versão</h3>
                  {selected.releaseItems.some(
                    (item) => item.itemType === 'WorkItem' || item.itemType === 'PullRequest',
                  ) ? (
                    <ActionIconButton
                      hint="Abre a lista completa dos itens do Azure."
                      onClick={() => setAzureItemsModalOpen(true)}
                    >
                      <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                    </ActionIconButton>
                  ) : null}
                </div>
                {selected.releaseItems.length === 0 ? (
                  <p className="text-sm text-secondary-500">Nenhum item no escopo ainda.</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto rounded-lg border border-secondary-100">
                    <ReleaseScopeItems
                      items={selected.releaseItems}
                      canRemove={canWrite && !releaseLocked && selected.status === 'Scheduled'}
                      onRemove={(item) => void handleDeleteItem(item)}
                    />
                  </div>
                )}
              </div>
              <DraggableModalPanel
                open={azureItemsModalOpen}
                title="Itens do Azure"
                subtitle={selected.releaseVersion ?? undefined}
                onClose={() => setAzureItemsModalOpen(false)}
                className="w-full max-w-3xl"
              >
                <ReleaseScopeItems
                  items={selected.releaseItems.filter(
                    (item) => item.itemType === 'WorkItem' || item.itemType === 'PullRequest',
                  )}
                  canRemove={canWrite && !releaseLocked && selected.status === 'Scheduled'}
                  onRemove={(item) => void handleDeleteItem(item)}
                />
              </DraggableModalPanel>

              {canWrite ? (
                <>
                  {!releaseLocked ? (
                  <>
                  <CollapsibleFormSection
                    title="Importar do Azure DevOps"
                    expanded={azureImportForm.formExpanded}
                    onToggle={() =>
                      azureImportForm.toggleOrCancel(() =>
                        setAzureImportState((current) => ({
                          ...current,
                          workItemIds: [...emptyIdRow],
                          pullRequestIds: [...emptyIdRow],
                        })),
                      )
                    }
                  >
                    <form
                      onSubmit={(event) => void handleAzureImport(event)}
                      className="grid gap-3"
                    >
                      <label className="erp-label">
                        Aplicação do pacote
                        <select
                          className="erp-input"
                          value={azureImportState.applicationId}
                          onChange={(event) =>
                            setAzureImportState((current) => ({
                              ...current,
                              applicationId: event.target.value,
                            }))
                          }
                          required
                        >
                          <option value="">Selecione</option>
                          {(
                            releases.find((item) => item.id === selected.releaseId)?.applications ??
                            []
                          ).map((app) => (
                            <option key={app.applicationId} value={app.applicationId}>
                              {app.applicationName ?? app.applicationId}
                            </option>
                          ))}
                        </select>
                      </label>
                      <AzureIdList
                        label="Work items"
                        values={azureImportState.workItemIds}
                        placeholder="1234"
                        buttonLabel="Adicionar work item"
                        addLabel="Inclui outro campo para um work item."
                        onChange={(workItemIds) =>
                          setAzureImportState((current) => ({ ...current, workItemIds }))
                        }
                      />
                      <AzureIdList
                        label="Pull requests"
                        values={azureImportState.pullRequestIds}
                        placeholder="88"
                        buttonLabel="Adicionar pull request"
                        addLabel="Inclui outro campo para uma pull request."
                        onChange={(pullRequestIds) =>
                          setAzureImportState((current) => ({ ...current, pullRequestIds }))
                        }
                      />
                      <p className="text-xs text-secondary-500">
                        A pull request também traz os work items associados no Azure. A aplicação
                        precisa ter organização/projeto (ou URL Azure) e PAT no Cofre.
                      </p>
                      <FormActionBar
                        className="erp-btn-actions"
                        onCancel={() =>
                          azureImportForm.collapseForm(() =>
                            setAzureImportState((current) => ({
                              ...current,
                              workItemIds: [...emptyIdRow],
                              pullRequestIds: [...emptyIdRow],
                            })),
                          )
                        }
                      >
                        <HintButton
                          type="submit"
                          hint="Busca no Azure os work items e as pull requests informados e inclui os que ainda não estão nesta release."
                          disabled={saving}
                          className="erp-btn-primary"
                        >
                          {saving ? 'Importando...' : 'Importar'}
                        </HintButton>
                      </FormActionBar>
                    </form>
                  </CollapsibleFormSection>

                  <div className="flex flex-wrap gap-2">
                    <HintButton
                      hint="Relê no Azure o título, a URL e o status dos work items e pull requests já incluídos nesta release. Work items associados a uma pull request entram no escopo quando ainda não foram informados."
                      className="erp-btn-secondary"
                      disabled={saving || selected.releaseItems.length === 0}
                      onClick={() => void handleAzureRefresh()}
                    >
                      Atualizar status Azure
                    </HintButton>
                  </div>

                  <CollapsibleFormSection
                    title="Adicionar item ao escopo"
                    expanded={itemForm.formExpanded}
                    onToggle={() =>
                      itemForm.toggleOrCancel(() =>
                        setItemState((current) => ({
                          ...current,
                          title: '',
                          externalId: '',
                          url: '',
                          itemType: 'Note',
                        })),
                      )
                    }
                  >
                    <form onSubmit={(event) => void handleAddItem(event)} className="grid gap-3">
                      <label className="erp-label">
                        Tipo
                        <select
                          className="erp-input"
                          value={itemState.itemType}
                          onChange={(event) =>
                            setItemState((current) => ({
                              ...current,
                              itemType: event.target.value,
                            }))
                          }
                        >
                          {TECHNOLOGY_RELEASE_ITEM_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="erp-label">
                        Título
                        <input
                          className="erp-input"
                          value={itemState.title}
                          onChange={(event) =>
                            setItemState((current) => ({
                              ...current,
                              title: event.target.value,
                            }))
                          }
                          required
                        />
                      </label>
                      <label className="erp-label">
                        ID externo
                        <input
                          className="erp-input"
                          value={itemState.externalId}
                          onChange={(event) =>
                            setItemState((current) => ({
                              ...current,
                              externalId: event.target.value,
                            }))
                          }
                          placeholder="Work item / PR"
                        />
                      </label>
                      <label className="erp-label">
                        URL
                        <input
                          className="erp-input"
                          value={itemState.url}
                          onChange={(event) =>
                            setItemState((current) => ({ ...current, url: event.target.value }))
                          }
                          placeholder="https://dev.azure.com/..."
                        />
                      </label>
                      <FormActionBar
                        className="erp-btn-actions"
                        onCancel={() =>
                          itemForm.collapseForm(() =>
                            setItemState((current) => ({
                              ...current,
                              title: '',
                              externalId: '',
                              url: '',
                              itemType: 'Note',
                            })),
                          )
                        }
                      >
                        <HintButton
                          type="submit"
                          hint="Inclui um work item, uma pull request ou uma nota no escopo desta release."
                          disabled={saving}
                          className="erp-btn-primary"
                        >
                          {saving ? 'Salvando...' : 'Adicionar'}
                        </HintButton>
                      </FormActionBar>
                    </form>
                  </CollapsibleFormSection>
                  </>
                  ) : null}

                  {selected.status === 'Scheduled' ? (
                    <div className="flex flex-wrap items-end gap-2 border-t border-secondary-100 pt-3">
                      <label className="erp-label min-w-[14rem] flex-1">
                        Reagendar
                        <input
                          type="datetime-local"
                          className="erp-input"
                          value={rescheduleLocal}
                          onChange={(event) => setRescheduleLocal(event.target.value)}
                        />
                      </label>
                      <ActionIconButton
                        hint="Salvar data. Grava a nova data planejada desta implantação. O horário é o do seu computador."
                        disabled={acting}
                        onClick={() => void handleReschedule()}
                      >
                        <CheckIcon className="h-5 w-5" />
                      </ActionIconButton>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          )}
        </section>
      </div>

      <DraggableModalPanel
        open={reopenTarget != null}
        title="Reabrir implantação"
        subtitle={
          reopenTarget
            ? `${reopenTarget.applicationName ?? 'Aplicação'} · ${reopenTarget.releaseVersion ?? ''}`
            : undefined
        }
        onClose={() => {
          if (acting) return;
          setReopenTarget(null);
          setReopenComment('');
        }}
        closeDisabled={acting}
        className="w-full max-w-md"
        footer={
          <FormActionBar
            onCancel={() => {
              if (acting) return;
              setReopenTarget(null);
              setReopenComment('');
            }}
          >
            <button
              type="submit"
              form="reopen-deployment-form"
              className="erp-btn-primary"
              disabled={acting || reopenComment.trim().length === 0}
            >
              {acting ? 'Reabrindo...' : 'Reabrir'}
            </button>
          </FormActionBar>
        }
      >
        <form id="reopen-deployment-form" onSubmit={(event) => void handleReopenSubmit(event)}>
          <label className="erp-label">
            Comentário
            <input
              className="erp-input"
              value={reopenComment}
              maxLength={REOPEN_COMMENT_MAX_LENGTH}
              onChange={(event) => setReopenComment(event.target.value)}
              placeholder="Motivo da reabertura"
              autoFocus
              required
            />
          </label>
          <p className="mt-1 text-xs text-secondary-500">
            Obrigatório. {reopenComment.length}/{REOPEN_COMMENT_MAX_LENGTH} caracteres. A implantação volta
            para agendada.
          </p>
        </form>
      </DraggableModalPanel>
    </PageShell>
  );
}
